import { useEffect, useCallback } from 'react';

// Global gtag declaration
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export const useWebsiteFeatures = () => {
  // Smooth scroll function
  const smoothScrollTo = useCallback((targetId: string) => {
    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      const offsetTop = targetElement.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  }, []);

  // Navbar scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.querySelector('.navbar');
      if (navbar) {
        if (window.scrollY > 100) {
          navbar.classList.add('scrolled');
        } else {
          navbar.classList.remove('scrolled');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Download tracking
  const trackDownload = useCallback((platform: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'download_app', {
        'platform': platform,
        'app_name': 'SobCof'
      });
    }
    console.log(`Download tracked: ${platform}`);
  }, []);

  // Form handling
  const handleContactForm = useCallback(async (formData: any) => {
    try {
      console.log('Contact form submitted:', formData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return { success: true, message: 'Mesajınız başarıyla gönderildi!' };
    } catch (error) {
      console.error('Form submission error:', error);
      return { success: false, message: 'Mesaj gönderilirken bir hata oluştu.' };
    }
  }, []);

  return {
    smoothScrollTo,
    trackDownload,
    handleContactForm
  };
};

// Cookie consent hook
export const useCookieConsent = () => {
  const acceptCookies = useCallback(() => {
    localStorage.setItem('cookieConsent', 'accepted');
    
    // Initialize analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  }, []);

  const rejectCookies = useCallback(() => {
    localStorage.setItem('cookieConsent', 'rejected');
    
    // Disable analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  }, []);

  return { acceptCookies, rejectCookies };
};

// Notification system hook
export const useNotification = () => {
  const showNotification = useCallback((message: string, type: 'info' | 'success' | 'error' | 'warning' = 'info') => {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.innerHTML = `
      <div class="notification-content">
        <i class="fas fa-${getNotificationIcon(type)}"></i>
        <span>${message}</span>
        <button onclick="this.closest('.notification').remove()" class="notification-close">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Show notification
    setTimeout(() => {
      notification.classList.add('show');
    }, 100);
    
    // Auto hide after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.classList.remove('show');
        setTimeout(() => {
          if (notification.parentNode) {
            notification.remove();
          }
        }, 300);
      }
    }, 5000);
  }, []);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success': return 'check-circle';
      case 'error': return 'exclamation-circle';
      case 'warning': return 'exclamation-triangle';
      default: return 'info-circle';
    }
  };

  return { showNotification };
};
